<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Badge from '@uiKit/components/Badge.vue'
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'
import BedIcon from '@uiKit/icons/BedIcon.vue'
import BathIcon from '@uiKit/icons/BathIcon.vue'
import { PROPERTY_STATUS, type PropertyStatus } from '../constants/propertyStatus'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import CardImageCarousel from './CardImageCarousel.vue'

interface Props {
  image: string
  /** Full gallery. When present, a swipeable carousel replaces the single image. */
  images?: string[]
  imageAlt?: string
  variant: PropertyStatus
  // Live
  isPremium?: boolean
  // Launching soon
  launchDate?: string
  launchDaysLeft?: number
  isBookmarked?: boolean
  // Fully funded
  isRented?: boolean
  fundedInLabel?: string
  // Shared
  area?: string
  propertyType?: string
  bedrooms?: number
  bathrooms?: number
  showChips?: boolean
  /** `light` = white disc with a brand-tinted glyph (saved-properties grid). */
  bookmarkAppearance?: 'dark' | 'light'
  /** Off where the card isn't a listing (order history cards). */
  showBookmark?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  images: () => [],
  imageAlt: '',
  isPremium: false,
  isBookmarked: false,
  isRented: false,
  showChips: false,
  bookmarkAppearance: 'dark',
  showBookmark: true,
})

const emit = defineEmits<{ (e: 'bookmark' | 'open'): void }>()

const hasGallery = computed(() => props.images.length > 0)

// Robust fallback — when the image URL is empty OR a request fails (broken URL,
// network failure, 404), render an inline icon placeholder instead of swapping
// to a remote placeholder image. Self-contained, never network-dependent.
const hasFailed = ref(false)
watch(
  () => props.image,
  () => {
    hasFailed.value = false
  },
)
const showPlaceholder = computed(() => hasFailed.value || !props.image)
const handleImgError = () => {
  hasFailed.value = true
}
</script>

<template>
  <Box class="card-image">
    <CardImageCarousel
      v-if="hasGallery"
      :images="images"
      :alt="imageAlt"
      class="card-image__carousel"
      @open="emit('open')"
    />
    <AppImage
      v-else-if="!showPlaceholder"
      :src="image"
      :alt="imageAlt"
      image-class="card-image__img"
      @error="handleImgError"
    />
    <Box
      v-else
      align="center"
      justify="center"
      class="card-image__placeholder"
      :aria-label="imageAlt || 'Property image unavailable'"
      role="img"
    >
      <PiIcon name="image" class="card-image__placeholder-icon" />
    </Box>

    <!-- Bottom-up scrim keeps the white spec chips legible on bright photos. -->
    <Box v-if="showChips" class="card-image__scrim" />

    <Box v-if="$slots.overlay" class="card-image__overlay">
      <slot name="overlay" />
    </Box>

    <!-- Caller-owned top-left overlay (e.g. the saved card's tokens-left chip). -->
    <Box v-if="$slots['top-left']" class="card-image__top-left">
      <slot name="top-left" />
    </Box>

    <!-- Live: premium badge only when flagged; Launching Soon gets no image
         badge (its title chip already shows "Launches in N days"). -->
    <Badge
      v-if="variant === PROPERTY_STATUS.LIVE && isPremium"
      label="Premium Location"
      icon="star-fill"
      color="amber"
      size="lg"
      class="card-image__badge-pos"
    />

    <!-- Fully Funded: keep "RENTED" as a status signal (it tells the user
         the asset is income-producing); drop the redundant "FUNDED" overlay
         since the tab itself already says Fully Funded. -->
    <Box
      v-if="variant === PROPERTY_STATUS.FULLY_FUNDED && isRented"
      gap="2"
      class="card-image__funded-badges"
    >
      <Badge label="RENTED" icon="home" color="cobalt" size="sm" :blur="true" />
    </Box>

    <!-- Bookmark — top right (all variants) -->
    <Box
      v-if="showBookmark"
      is="button"
      align="center"
      justify="center"
      class="card-image__bookmark"
      :class="`card-image__bookmark--${bookmarkAppearance}`"
      type="button"
      :aria-label="isBookmarked ? 'Remove from saved properties' : 'Save property for later'"
      :aria-pressed="isBookmarked"
      :title="isBookmarked ? 'Saved' : 'Save property'"
      @click.stop="emit('bookmark')"
    >
      <PiIcon :name="isBookmarked ? 'bookmark-fill' : 'bookmark'" />
    </Box>

    <!-- Spec chips — bottom row (opt-in via showChips) -->
    <Box
      v-if="showChips && (area || propertyType || bedrooms || bathrooms)"
      gap="2"
      class="card-image__chips"
    >
      <Text
        v-if="area"
        type="body"
        weight="medium"
        is="span"
        color="rgb(255, 255, 255)"
        class="card-image__chip"
      >
        <PiIcon name="expand" />{{ area }}
      </Text>
      <Text
        v-if="propertyType"
        type="body"
        weight="medium"
        is="span"
        color="rgb(255, 255, 255)"
        class="card-image__chip"
      >
        <PiIcon name="building" />{{ propertyType }}
      </Text>
      <Text
        v-if="bedrooms"
        type="body"
        weight="medium"
        is="span"
        color="rgb(255, 255, 255)"
        class="card-image__chip"
        :aria-label="`${bedrooms} bedrooms`"
      >
        <BedIcon class="card-image__chip-svg" />{{ bedrooms }}
      </Text>
      <Text
        v-if="bathrooms"
        type="body"
        weight="medium"
        is="span"
        color="rgb(255, 255, 255)"
        class="card-image__chip"
        :aria-label="`${bathrooms} bathrooms`"
      >
        <BathIcon class="card-image__chip-svg" />{{ bathrooms }}
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.card-image {
  position: relative;
  width: 100%;
  height: 208px;
  overflow: hidden;
  flex-shrink: 0;
  /* Neutral surface beneath the image — also covers the brief moment between
     load-failure and the placeholder swap so the card never flashes white. */
  background: var(--p-surface-100);
}

.card-image :deep(.card-image__img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  /* Hide the broken-image icon + alt-text the browser draws when src fails;
     the @error handler will swap to the icon placeholder branch first, but
     this is the belt-and-braces guard for the in-between frame. */
  color: transparent;
  font-size: 0;
}

/* Icon placeholder — fills the image bounds with a neutral surface and a
   centered image-glyph; replaces the remote text-based placeholder. */
.card-image__placeholder {
  position: absolute;
  inset: 0;
  background: var(--p-surface-100);
}

.card-image__placeholder-icon {
  font-size: 32px;
  color: var(--p-surface-400);
}

/* ── Badge positioning ── */
.card-image__badge-pos {
  position: absolute;
  top: 12px;
  left: 12px;
}

/* ── Fully Funded stacked badges ── */
.card-image__funded-badges {
  position: absolute;
  top: 16px;
  left: 16px;
}

/* ── Bookmark — top-right ── */
.card-image__bookmark {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  /* Fixed light colours — sits on a photo, so it stays light in dark mode. */
  background: rgb(250, 250, 250);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: rgb(10, 10, 10);
  box-shadow:
    0 1px 1.5px rgba(0, 0, 0, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1);
  transition: transform 0.15s ease;
}

.card-image__bookmark:hover {
  transform: scale(1.05);
}

/* Larger invisible hit area — 32px visual, 44px touch target. */
.card-image__bookmark::before {
  content: '';
  position: absolute;
  inset: -6px;
}

.card-image__scrim {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.76) 4%,
    rgba(0, 0, 0, 0) 52%,
    rgba(0, 0, 0, 0.1) 100%
  );
}

/* Overlays over the carousel don't take pointer events, so a swipe or tap
   that starts on a chip still reaches the photos underneath. */
.card-image__overlay {
  position: absolute;
  top: 14px;
  left: 12px;
  right: 12px;
  pointer-events: none;
}

.card-image__carousel {
  position: absolute;
  inset: 0;
}

.card-image__bookmark--light {
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  background: var(--p-surface-50);
  backdrop-filter: none;
  color: var(--p-primary-600);
  box-shadow:
    0 1px 1.5px rgba(0, 0, 0, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1);
}

.card-image__bookmark--light:hover {
  background: var(--p-surface-0);
}

.card-image__bookmark:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.card-image__bookmark :deep(.pi) {
  font-size: 16px;
}

/* ── Top-left overlay slot ── */
.card-image__top-left {
  position: absolute;
  top: 14px;
  left: 12px;
  right: 60px;
}

/* ── Spec chips — bottom row, frosted glass ── */
.card-image__chips {
  position: absolute;
  bottom: 16px;
  left: 12px;
  right: 12px;
  display: flex;
  flex-wrap: wrap-reverse;
  gap: 8px;
  pointer-events: none;
}

/* Frosted-glass spec chips over the photo. */
.card-image__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.29);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(6px);
  padding: 0 12px;
  height: 32px;
  border-radius: 14px;
  white-space: nowrap;
  line-height: 18px;
}

.card-image__chip :deep(.pi) {
  font-size: 13px;
  color: var(--p-surface-0);
}

.card-image__chip-svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--p-surface-0);
}

/* Phone widths: tighter chips so the spec row stays on one line. */
@media (max-width: 640px) {
  .card-image__chips {
    gap: 6px;
  }
  .card-image__chip {
    gap: 4px;
    padding: 0 8px;
  }
}
</style>
